// ============================================================
// a11y.js — accesibilidad compartida por los RENDERERS de bloque.
//
// POR QUÉ EXISTE: los bloques con imagen (hero, content, features, gallery, team) las pintaban sin
// `alt`, o con un `alt` escapado a medias (`replace(/"/g,'&quot;')`). Las dos cosas hacen daño:
//   · sin `alt`, un lector de pantalla lee la URL del archivo ("photo-1504674900247-0877df9cc836")
//     y Google se queda sin la única descripción textual de esa imagen;
//   · con el escape a medias, una descripción escrita en el editor (que es innerHTML de un
//     contenteditable, así que puede traer <b>, <span style> o &nbsp;) se cuela con etiquetas
//     dentro del atributo y se lee en voz alta tal cual.
//
// EL TEXTO SALE SIEMPRE DEL CONTENIDO DEL CLIENTE (título del bloque, descripción de la foto,
// nombre de la persona). NUNCA una cadena fija en español: la web puede estar en cualquier idioma
// y ese texto lo escribe el cliente o lo genera la IA. Si no hay contenido del que tirar, el alt se
// deja VACÍO a propósito (`alt=""`): eso marca la imagen como decorativa y los lectores de pantalla
// la SALTAN, que es justo lo correcto para una foto de ambiente — y sigue siendo mejor que omitir
// el atributo, que los obliga a leer el nombre del archivo.
//
// Escrito en ES2019 (sin `?.` ni `??`) como el resto de utils puros: el node del entorno (v12) parsea
// estos módulos en los harness de test.
// ============================================================
import { escapeHtml } from './dom.js';

// Tope del alt. Las guías de accesibilidad piden descripciones BREVES (un lector de pantalla no
// interrumpible recitando un párrafo entero es peor que no tener alt).
export const ALT_MAX = 120;

const _TAGS = /<[^>]*>/g;
const _ENTIDADES = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" };

/**
 * Texto plano de un campo editable: quita el marcado que deja el contenteditable, deshace las
 * entidades básicas y colapsa los espacios. Devuelve '' para null/undefined/no-string.
 */
export function textoPlano(valor) {
    if (valor == null) return '';
    return String(valor)
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(_TAGS, '')
        .replace(/&(nbsp|amp|lt|gt|quot|#39|#x27);/gi, function (m, ent) {
            const v = _ENTIDADES[String(ent).toLowerCase()];
            return v === undefined ? ' ' : v;
        })
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Atributo `alt="…"` listo para interpolar en el HTML del bloque, a partir de uno o varios
 * candidatos de contenido (se usa el primero con texto). Sin ninguno → `alt=""` (decorativa).
 *
 * Uso:  `<img src="…" ${altAttr(item.caption)}>`  ·  `<img … ${altAttr(p.image_alt, p.title)}>`
 */
export function altAttr(...candidatos) {
    for (let i = 0; i < candidatos.length; i++) {
        const txt = textoPlano(candidatos[i]).slice(0, ALT_MAX);
        if (txt) return 'alt="' + escapeHtml(txt) + '"';
    }
    return 'alt=""';
}
